import { inject, Injectable } from '@angular/core';
import { FacebookPixelService } from './facebook-pixel.service';
import { AmplitudeService } from './amplitude.service';

/**
 * Orchestrator service for all app-level business events.
 * Delegates to FB Pixel and Amplitude.
 * Components should call this service instead of individual analytics services.
 */
@Injectable({
  providedIn: 'root',
})
export class AppEventsService {
  private readonly facebookPixel = inject(FacebookPixelService);
  private readonly amplitude = inject(AmplitudeService);

  /**
   * User completed registration (works for both email and OAuth sign-ups).
   * Fires: FB Pixel Lead, Amplitude sign_up_completed
   *
   * @param source - Analytics source inferred from the redirect URL
   * @param eventId - Deduplication event ID returned by backend CAPI.
   *                  When provided, the same ID is sent with the browser pixel
   *                  so Facebook deduplicates the server + browser events.
   *                  Falls back to a client-generated UUID if not provided.
   */
  signUpCompleted(source: string, eventId?: string): void {
    const id = eventId ?? this.generateEventId();

    // FB Pixel - Lead event with eventID for deduplication with server-side CAPI
    this.facebookPixel.trackEvent('Lead', {}, { eventID: id });

    // Amplitude
    this.amplitude.trackEvent('sign_up_completed', { source });
  }

  private generateEventId(): string {
    return crypto.randomUUID();
  }
}
